import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { Pencil, Trash2, MessagesSquare } from "lucide-react";
import { EmptyState, GlassCard, PersonAvatar, T, TYPE, formatDateTime, formatRelative, primaryButtonSx } from "../../user/complaints/shared";
import { useSocketEvent } from "../../helpdesk/socket";
import { pm, errorMessage } from "../api";
import { usePm } from "../PmContext";
import RichEditor, { RichView } from "../RichEditor";
import { has } from "../ui";

/** Project-wide conversation (not tied to an issue). Realtime via the project room. */
const Discussion = ({ project }) => {
  const ctx = usePm();
  const [rows, setRows] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [body, setBody] = useState("");
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const bottom = useRef(null);
  const canPost = has(project, "issue.comment") && !project.archivedAt;

  const load = useCallback(async (before) => {
    try {
      const r = await pm.discussion(project._id, { before, limit: 50 });
      setRows((cur) => (before ? [...r.data, ...(cur || [])] : r.data));
      setHasMore(r.hasMore);
      if (!before) setTimeout(() => bottom.current?.scrollIntoView({ block: "end" }), 50);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [project._id]);
  useEffect(() => {
    load();
  }, [load]);
  useSocketEvent("pm.discussion.message", (m) => {
    if (String(m.projectId) !== String(project._id)) return;
    setRows((cur) => {
      const list = cur || [];
      if (m.deletedAt) return list.filter((x) => x._id !== m._id);
      const i = list.findIndex((x) => x._id === m._id);
      if (i >= 0) return list.map((x) => (x._id === m._id ? m : x));
      return [...list, m];
    });
    setTimeout(() => bottom.current?.scrollIntoView({ block: "end", behavior: "smooth" }), 50);
  });

  const send = async () => {
    if (!body) return;
    setSending(true);
    try {
      if (editing) await pm.editMessage(project._id, editing, body);
      else await pm.postMessage(project._id, body);
      setBody("");
      setEditing(null);
    } catch (err) {
      ctx.fail(err, "Could not send");
    } finally {
      setSending(false);
    }
  };

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!rows) return <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={24} /></Box>;
  return (
    <Box sx={{ maxWidth: 900 }}>
      <GlassCard sx={{ p: 2, mb: 2, maxHeight: "60vh", overflowY: "auto" }}>
        {hasMore && <Box sx={{ textAlign: "center", mb: 1 }}><Button size="small" onClick={() => load(rows[0]._id)}>Load earlier</Button></Box>}
        {!rows.length && <EmptyState dense icon={MessagesSquare} title="Start the conversation" description="Announcements, decisions and questions for the whole project. Use @ to mention people or roles." />}
        {rows.map((m, i) => {
          const own = String(m.author?.id) === String(ctx.me.id);
          const grouped = i > 0 && String(rows[i - 1].author?.id) === String(m.author?.id) && new Date(m.createdAt) - new Date(rows[i - 1].createdAt) < 5 * 60000;
          return (
            <Stack key={m._id} direction="row" spacing={1.25} sx={{ pt: grouped ? 0.25 : 1.25, "&:hover .msg-actions": { opacity: 1 } }}>
              <Box sx={{ width: 32, flexShrink: 0 }}>{!grouped && <PersonAvatar name={m.author?.name} size={32} />}</Box>
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                {!grouped && (
                  <Stack direction="row" spacing={1} alignItems="baseline">
                    <Typography sx={{ fontWeight: 700, fontSize: TYPE.small }}>{m.author?.name}</Typography>
                    <Tooltip title={formatDateTime(m.createdAt)}><Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{formatRelative(m.createdAt)}</Typography></Tooltip>
                  </Stack>
                )}
                <Stack direction="row" alignItems="flex-start">
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}><RichView html={m.body} />{m.editedAt && <Typography sx={{ fontSize: "0.66rem", color: T.inkMuted }}>(edited)</Typography>}</Box>
                  <Stack direction="row" className="msg-actions" sx={{ opacity: 0, transition: "opacity .15s" }}>
                    {own && canPost && <IconButton size="small" onClick={() => { setEditing(m._id); setBody(m.body); }}><Pencil size={13} /></IconButton>}
                    {(own || has(project, "project.manage_settings")) && !project.archivedAt && <IconButton size="small" onClick={() => window.confirm("Delete this message?") && pm.deleteMessage(project._id, m._id).catch((err) => ctx.fail(err))}><Trash2 size={13} /></IconButton>}
                  </Stack>
                </Stack>
              </Box>
            </Stack>
          );
        })}
        <div ref={bottom} />
      </GlassCard>
      {canPost && (
        <Box>
          <RichEditor value={body} onChange={setBody} minHeight={70} placeholder="Message the project… (@ to mention, Ctrl+Enter to send)" onSubmit={send}
            onUpload={(files) => pm.uploadFiles(project._id, files, "Discussion")} mentionRoles={ctx.projectRoles} />
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button variant="contained" sx={primaryButtonSx} disabled={!body || sending} onClick={send}>{editing ? "Save" : "Send"}</Button>
            {editing && <Button onClick={() => { setEditing(null); setBody(""); }}>Cancel</Button>}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default Discussion;
